
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import SocTable from "@/components/SocTable/SocTable.vue";
import InlineLoader from "@/components/InlineLoader/InlineLoader.vue";
import { API_ROUTES } from "@/utils/API";
import { API_TSocItem } from "@/models/soc_items";
import { debounce } from "throttle-debounce-ts";
import { TAnswerBlock } from "@/modules/Questionnaire/components/AnswerBlocks/TAnswerBlocks";
import { SMQuestionnaire } from "@/store";
import axios, { CancelTokenSource } from "axios";

const ICONS = {
  search: "search",
  close: "close-circle",
};

const ITEMS_LIMIT = 100;

const MIN_QUERY_LENGTH = 3;

const DEBOUNCE_TIMEOUT = 500;

@Component({
  components: {
    SocTable,
    InlineLoader,
  },
})
export default class AnswerBlockJobSearch extends Vue {
  @Prop({ type: Object, default: () => ({}) }) readonly answerBlockData!: TAnswerBlock;
  @Prop({ type: Number, default: () => 0 }) readonly index: number | undefined;
  @Prop({ type: Number, default: () => 0 }) readonly loopIndex: number | undefined;

  icons = ICONS;

  get inputProps() {
    return {
      label: this.$tc("placeholders.roleName"),
      iconPrepend: ICONS.search,
      iconAppend: ICONS.close,
      iconAppendHidden: ["empty", "unfocused"],
    };
  }

  items: API_TSocItem[] = [];

  query = "";

  input: API_TSocItem | undefined | unknown = "";

  loading = false;

  @Watch("input")
  updateAnswer(value: string) {
    const { index, answerBlockData, loopIndex } = this;

    if (index == null) return;

    SMQuestionnaire.updateAnswer({
      index,
      value,
      required: answerBlockData.is_required,
      answer_block_id: answerBlockData.ID,
      loop_index: loopIndex,
      condition: answerBlockData.condition,
    });

    if (answerBlockData?.alias) this.setLocalStorageVariable(answerBlockData.alias);
  }

  setLocalStorageVariable(alias: string) {
    window.sessionStorage.setItem(`alias.${alias}`, this.query);
  }

  searchItemsDebounced = debounce(DEBOUNCE_TIMEOUT, (q: string) => this.fetchItems(q));

  requestId = 0;

  cancelSource: CancelTokenSource | null = null;

  async fetchItems(query: string) {
    if (this.loading) return;

    const q = query.trim();

    if (!this.textTypeCheck(q)) {
      this.items = [];
      return;
    }

    if (this.cancelSource) this.cancelSource.cancel("New search started");

    this.cancelSource = axios.CancelToken.source();

    const currentId = ++this.requestId;

    this.loading = true;

    try {
      const { data } = await this.axios.get<API_TSocItem[]>(
        API_ROUTES.SEARCH_SOC_CODE(q, 0, ITEMS_LIMIT),
        { cancelToken: this.cancelSource.token }
      );

      if (currentId !== this.requestId) return;

      this.items = data ?? [];
    } catch (err) {
      if (!axios.isCancel(err)) {
        this.items = [];
      }
    } finally {
      if (currentId === this.requestId) this.loading = false;
    }
  }

  searchItems(query: string) {
    this.searchItemsDebounced(query);
  }

  get filteredItems(): API_TSocItem[] {
    const map = new Map<string, API_TSocItem>();

    for (const it of this.items) {
      if (!map.has(it.soc_code)) map.set(it.soc_code, it);
    }

    return Array.from(map.values());
  }

  get counterLabel(): string {
    const count = this.filteredItems.length;

    return count
      ? `${count} ${count === 1 ? this.$tc("counter.foundOne") : this.$tc("counter.foundMany")}`
      : this.$tc("counter.notFound");
  }

  textTypeCheck(val: string): boolean {
    return !!val && val.trim().length >= MIN_QUERY_LENGTH;
  }

  @Watch("query")
  onQueryUpdate(val: string): void {
    if (this.suppressQueryWatch) return;

    const q = (val || "").trim();

    if (!this.textTypeCheck(q)) {
      this.items = [];

      return;
    }
    this.searchItems(q);
  }

  preselected = "";

  cleanSearch() {
    this.query = "";
    this.items = [];
    this.input = "";
    this.preselected = "";
  }

  async created() {
    SMQuestionnaire.setCanGoNext(!SMQuestionnaire.questionIsRequired);

    const answer = SMQuestionnaire.answers.find(
      (a) => a.answer_block_id === this.answerBlockData?.ID && this.loopIndex === a.loop_index
    );

    if (!answer || !answer.value || answer.value === "0") return;

    this.preselected = String(answer.value).trim();
    this.suppressQueryWatch = true;
    this.query = this.preselected;
    this.suppressQueryWatch = false;
  }

  suppressQueryWatch = false;

  @Watch("filteredItems")
  onFilteredItemsReady() {
    if (!this.preselected) return;

    const found =
      this.filteredItems.find((i) => String(i.soc_code) == this.preselected) ||
      this.filteredItems.find((i) => i.soc_name == this.preselected);

    if (!found) return;

    this.suppressQueryWatch = true;
    this.query = found.soc_name;
    this.input = String(found.soc_code);
    this.$nextTick(() => {
      this.suppressQueryWatch = false;
    });
  }

  beforeDestroy() {
    if (this.cancelSource) this.cancelSource.cancel("Component destroyed");

    if (this.searchItemsDebounced && (this.searchItemsDebounced as any).cancel) {
      (this.searchItemsDebounced as any).cancel();
    }
  }
}
